import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ChartBar, Download, Funnel, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [branches, setBranches] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [branchPerformance, setBranchPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [branchesRes, statusesRes, perfRes] = await Promise.all([
        axios.get(`${API_URL}/api/branches`),
        axios.get(`${API_URL}/api/statuses`),
        user?.role === 'super_admin' ? axios.get(`${API_URL}/api/analytics/branch-performance`) : Promise.resolve({ data: [] })
      ]);
      setBranches(branchesRes.data);
      setStatuses(statusesRes.data);
      setBranchPerformance(perfRes.data);
    } catch (e) {
      console.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.append('branch_id', selectedBranch);
      if (selectedStatus) params.append('status', selectedStatus);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const { data } = await axios.get(`${API_URL}/api/analytics/reports?${params}`);
      setRecords(data.records);
      toast.success(`Found ${data.total} records`);
    } catch (e) {
      toast.error('Failed to generate report');
    }
  };

  const handleExport = () => {
    if (records.length === 0) {
      toast.error('No records to export');
      return;
    }

    const headers = ['ID', 'Status', 'Branch', 'Created At', ...Object.keys(records[0]?.data || {})];
    const rows = records.map(r => [
      r.id,
      r.status,
      r.branch_id,
      r.created_at,
      ...Object.values(r.data || {})
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'report.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Report exported');
  };

  if (loading) {
    return <div className="h-96 skeleton rounded-md"></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">Reports</h1>
        <p className="text-muted-foreground">Generate and export reports</p>
      </div>

      {/* Branch Performance (Super Admin only) */}
      {user?.role === 'super_admin' && branchPerformance.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ChartBar size={20} className="text-primary" />
              Branch Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Branch</TableHead>
                  <TableHead>Total Records</TableHead>
                  <TableHead>Closed Records</TableHead>
                  <TableHead>Conversion Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branchPerformance.map(perf => (
                  <TableRow key={perf.branch_id}>
                    <TableCell className="font-medium">
                      {perf.branch_name}
                      <Badge variant="outline" className="ml-2">{perf.branch_code}</Badge>
                    </TableCell>
                    <TableCell>{perf.total_records}</TableCell>
                    <TableCell>{perf.closed_records}</TableCell>
                    <TableCell>
                      <Badge variant={perf.conversion_rate > 50 ? 'default' : 'secondary'}>
                        {perf.conversion_rate}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Report Generator */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Funnel size={20} className="text-primary" />
            Generate Report
          </CardTitle>
          <CardDescription>Filter and export closed cases</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {user?.role === 'super_admin' && (
              <div className="space-y-2">
                <Label>Branch</Label>
                <Select value={selectedBranch || "all"} onValueChange={(v) => setSelectedBranch(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="report-branch">
                    <SelectValue placeholder="All branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={selectedStatus || "all"} onValueChange={(v) => setSelectedStatus(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-status">
                  <SelectValue placeholder="Closed statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All closed</SelectItem>
                  {statuses.filter(s => s.is_closed).map(s => (
                    <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="report-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="report-end-date"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleGenerateReport} data-testid="generate-report-btn">
              Generate Report
            </Button>
            {records.length > 0 && (
              <Button variant="outline" onClick={handleExport} data-testid="export-report-btn">
                <Download size={16} className="mr-2" />
                Export CSV
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {records.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Results ({records.length} records)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data Preview</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.slice(0, 50).map(record => (
                    <TableRow key={record.id}>
                      <TableCell className="font-mono text-xs">{record.id.slice(0, 8)}...</TableCell>
                      <TableCell>
                        <Badge variant="outline">{record.status}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm">
                        {Object.values(record.data || {}).slice(0, 3).join(', ')}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(record.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {records.length > 50 && (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Showing first 50 records. Export for full data.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
